import { ActivationState, Client, Frame } from '@stomp/stompjs';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import type { IConversationMessage } from '../algebra/conversations';
import { EventFormat, EventTypes } from '../algebra/global';
import type { IQueuePosition } from '../algebra/waiting-room';
import useGetSurvivorsConversation, { SurvivorsConversationStore } from '../datastores/useGetSurvivorsConversation';
import { MessagingTopics } from '../prelude/constants/topics';
import type { ISocketContext } from '../prelude/context/socket';
import { createLogger } from '../prelude/logger';
import useOnlineStatus from './useOnlineStatus';
import { v4 as uuid } from 'uuid';

const fileLabel = 'useSocket.tsx';
const logger = createLogger({
  fileLabel,
});

const useSocket = (task_sid: string, messaging_service_key: string, captchaToken = '', queue_sid: string): ISocketContext => {
  const [socketClient, setSocketClient] = useState<Client>(new Client());
  const [socketConnectionError, SetSocketConnectionError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [connected, setConnected] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const [maxAttemptConnections, _] = useState<number>(10);
  const [currentTry, setCurrentTry] = useState<number>(0);
  const [connectionMsg, setConnectionMsg] = useState<string>('');
  const [retrying, setReTrying] = useState<boolean>(false);
  const { cancelled, ended, sessionId } = useGetSurvivorsConversation();
  /**
   * Function called when reconnecting.
   */
  const reconnect = () => {
    setCurrentTry(0);
    socketClient.activate();
  };
  /**
   * UpdateQueue Position
   * @param message
   */
  const updateDynamicQueuePosition = useCallback(
    (message: EventFormat<number>) => {
      //udpdating posiiton in cache
      queryClient.setQueriesData(['queuePosition'], (oldData) => {
        const update = (data: IQueuePosition) => {
          if (message.event === EventTypes.DEQUEUE_TASK) {
            return {
              ...data,
              // eslint-disable-next-line no-magic-numbers
              position: data.position > message.data ? (data.position - 1 > 0 ? data.position - 1 : 0) : data.position,
            };
          } else {
            //some one got back in queue - so incrementing.
            return {
              ...data,
              // eslint-disable-next-line no-magic-numbers
              position: data.position + 1,
            };
          }
        };
        return update(oldData as IQueuePosition);
      });
    },
    [queryClient]
  );

  useEffect(() => {
    if (cancelled || ended) {
      socketClient?.deactivate();
    }
  }, [cancelled, ended]);

  const updateConversationMessages = useCallback((message: EventFormat<IConversationMessage>) => {
    logger.debug('Message recieived>', message.data);
    if (message.event === EventTypes.WEBCHAT_END) {
      SurvivorsConversationStore.endConversation();
    } else if (message.event === EventTypes.WEBCHAT_CANCEL) {
      SurvivorsConversationStore.cancelConversation();
    }
    SurvivorsConversationStore.updateMessages(message.data as unknown as IConversationMessage);
  }, []);

  const updateIsTypingStatus = useCallback(
    (message: { id: string; user: string; isTyping: boolean }) => {
      if (message.id === task_sid || message.user === 'survivor') return;
      if (message.isTyping) SurvivorsConversationStore.setIsTyping(`${message.user} is typing`);
      else SurvivorsConversationStore.setIsTyping('');
    },
    [task_sid]
  );

  const onWebSocketBeforeConnect = useCallback(() => {
    setCurrentTry((prevTry) => prevTry + 1);
  }, [currentTry]);

  useEffect(() => {
    if (currentTry > maxAttemptConnections) {
      setConnectionMsg(`Disconnected after ${maxAttemptConnections} attempts`);
      setReTrying(false);
      socketClient.deactivate();
      SetSocketConnectionError('Connection Broken, Click here to manually Connect');
    }
  }, [currentTry, socketClient]);

  useEffect(() => {
    if (!socketClient) {
      return;
    }
    const interval = setInterval(() => {
      socketClient.connected &&
        socketClient.publish({
          destination: `/app/ping`,
          body: task_sid,
        });
    }, 15000);
    return () => clearInterval(interval);
  }, [task_sid, socketClient]);

  /**
   *
   * @param frame
   */
  const onWebSocketConnected = useCallback(
    (frame: Frame) => {
      setConnected(true);
      setCurrentTry(0);
      SetSocketConnectionError('');
      setLoading(false);
      setSocketClient(socketClient);
      SurvivorsConversationStore.setSurvivorIdAndMessagingServiceKey(task_sid, messaging_service_key);
      logger.info('WebSocket Connected : ', frame.headers.message);
      //1. Queue Position
      socketClient.subscribe(`${MessagingTopics.WEBCHAT_POSITION}/${queue_sid}`, (message) => updateDynamicQueuePosition(JSON.parse(message.body)));
      //2.  Task Messages Update
      socketClient.subscribe(
        `/queue/Consumer.${sessionId}.VirtualTopic.webchat.task-conversation.${task_sid}`,
        (message) => {
          updateConversationMessages(JSON.parse(message.body));
          message.ack();
        },
        {
          id: `task-${task_sid}`,
          ack: 'client',
        }
      );
      //3. Is Typing Notification
      socketClient.subscribe(`/topic${MessagingTopics.TASK_CONVERSATION_IS_TYPING}${task_sid}`, (message) => updateIsTypingStatus(JSON.parse(message.body)));
      //4. particular task status
      socketClient.subscribe(`${MessagingTopics.TASK_STATUS}`, (msg) => {
        const message = JSON.parse(msg.body);
        if (message.event === EventTypes.WEBCHAT_END) {
          SurvivorsConversationStore.endConversation();
          socketClient.deactivate();
        } else if (message.event === EventTypes.WEBCHAT_CANCEL) {
          SurvivorsConversationStore.cancelConversation();
          socketClient.deactivate();
        }
      });
      //5. ping-pong
      socketClient.subscribe('/user/queue/pong', (message) => {
        console.log('Received pong', JSON.stringify(message.body));
      });
    },
    [messaging_service_key, queue_sid, sessionId, socketClient, task_sid, updateConversationMessages, updateDynamicQueuePosition, updateIsTypingStatus]
  );
  /**
   *
   * @param evt
   */
  const onWebSocketClose = useCallback(
    (evt: SyntheticEvent) => {
      if (socketClient.state !== ActivationState.INACTIVE) {
        setConnected(false);
        setLoading(false);
        setReTrying(true);
        socketClient.reconnectDelay = 200 * Math.pow(2, currentTry);
        setConnectionMsg(`Reconnecting...`);
        logger.info('Closed Event', evt);
      }
    },
    [currentTry]
  );

  /**
   *
   * @param debug
   */
  const onWebSocketDebug = useCallback((debug: string) => {
    logger.info(new Date(), debug);
  }, []);

  /**
   *
   * @param frame
   */
  const onWebSocketError = useCallback((frame: Frame) => {
    SetSocketConnectionError(`Broker reported error: ${frame.headers.message}`);
    setConnected(false);
    logger.info(`Broker reported error: ${frame.headers.message}`);
  }, []);

  /**
   *
   */
  const onWebSocketDisconnected = useCallback(() => {
    setConnected(false);
    setLoading(false);
    logger.info(`Disconnected at ${new Date()}`);
  }, []);

  //Connection : Connect Only once logged In..
  useEffect(() => {
    if (isOnline) {
      setLoading(true);
      socketClient.configure({
        brokerURL: process.env.NX_WS_HOTLINES_URL,
        reconnectDelay: 0,
        heartbeatIncoming: 15000,
        heartbeatOutgoing: 15000,
        connectionTimeout: 0,
      });
      socketClient.activate();
    }
    return () => {
      socketClient.deactivate();
    };
  }, [socketClient, isOnline]);

  useEffect(() => {
    if (socketClient) {
      socketClient.beforeConnect = onWebSocketBeforeConnect;
    }
  }, [onWebSocketBeforeConnect, socketClient, currentTry]);

  useEffect(() => {
    if (socketClient) {
      socketClient.onConnect = onWebSocketConnected;
    }
  }, [onWebSocketConnected, socketClient]);

  useEffect(() => {
    if (socketClient) {
      socketClient.onWebSocketClose = onWebSocketClose;
    }
  }, [onWebSocketClose, socketClient]);

  useEffect(() => {
    if (socketClient) {
      socketClient.debug = onWebSocketDebug;
    }
  }, [onWebSocketDebug, socketClient]);

  useEffect(() => {
    if (socketClient) {
      socketClient.onStompError = onWebSocketError;
    }
  }, [onWebSocketError, socketClient]);

  useEffect(() => {
    if (socketClient) {
      socketClient.onDisconnect = onWebSocketDisconnected;
    }
  }, [onWebSocketDisconnected, socketClient]);
  const generateUUID = () => {
    const sessionId = uuid();
    SurvivorsConversationStore.storeSessionId(sessionId);
    return sessionId;
  };
  useEffect(() => {
    if (socketClient) {
      socketClient.connectHeaders = {
        OWASP_CSRFGUARD: '',
        'g-recaptcha-response': captchaToken,
        survivor_task_sid: task_sid,
        messaging_service_key: messaging_service_key,
        'client-id': generateUUID(),
      };
    }
  }, [captchaToken, messaging_service_key, socketClient, task_sid]);

  return {
    client: socketClient,
    loading,
    error: socketConnectionError,
    connected,
    connectionMsg,
    reconnect,
    retrying,
  };
};

export default useSocket;
